/* Cart drawer suggestions (snippets/lf-cart-upsell.liquid, sections/lf-cart-upsell.liquid).

   Loads only while the cart drawer is open, so a closed drawer on every page
   costs no requests. Complementary recommendations are asked for first; the
   store has none configured for most products today, so related fills the
   rest. Products already in the cart are dropped here rather than in Liquid, so
   a cached response stays correct as the cart changes.

   Add posts /cart/add.js from a plain button, never a /cart/add form. Rebuy's
   Smart Cart attaches a submit listener to every such form and adds the item a
   second time. The click handler also stops immediate propagation in case
   another script listens on the same button. After the add, the theme is told
   the cart changed with a CartLinesUpdateEvent that carries the re-rendered
   drawer section, the same way assets/lf-pro-quick-order.js does it, and
   cart-items-component morphs the drawer. */
import { CartLinesUpdateEvent } from '@shopify/events';

const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
const MAX_ALLOWED = 3;
const SEEDS = 3;

class LfCartUpsell extends HTMLElement {
  #loadedKey = '';
  #cache = new Map();
  /** @type {MutationObserver | null} */
  #observer = null;
  /** @type {Element | null} */
  #drawer = null;

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    this.#drawer = this.closest('theme-drawer');
    this.#drawer?.addEventListener('theme-drawer:open', this.#onOpen);
    this.#observer = new MutationObserver(() => {
      if (this.#isOpen()) this.#load();
    });
    this.#observer.observe(this, { attributes: true, attributeFilter: ['data-product-ids', 'data-max'] });
    if (this.#isOpen()) this.#load();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.#drawer?.removeEventListener('theme-drawer:open', this.#onOpen);
    this.#observer?.disconnect();
  }

  #onOpen = () => this.#load();

  #isOpen() {
    const drawer = /** @type {any} */ (this.#drawer);
    if (!drawer) return true;
    return Boolean(drawer.isOpen) || drawer.hasAttribute('open');
  }

  async #load() {
    const ids = (this.dataset.productIds || '').split(',').filter(Boolean);
    // data-max 0 means "no suggestions" (a full cart with that setting at 0).
    const requested = parseInt(this.dataset.max || '', 10);
    const max = Number.isNaN(requested) ? MAX_ALLOWED : Math.min(MAX_ALLOWED, Math.max(0, requested));
    const key = `${ids.join(',')}|${max}`;
    if (key === this.#loadedKey) return;
    this.#loadedKey = key;

    if (!ids.length || max === 0) {
      this.#show([]);
      return;
    }

    const exclude = new Set(ids);
    /** @type {Map<string, Element>} */
    const picked = new Map();
    /** @param {Document} doc */
    const take = (doc) => {
      for (const item of doc.querySelectorAll('[data-lf-upsell-product]')) {
        if (picked.size >= max) return;
        const id = /** @type {HTMLElement} */ (item).dataset.productId || '';
        if (!exclude.has(id) && !picked.has(id)) picked.set(id, item);
      }
    };

    const seeds = ids.slice(0, SEEDS);
    try {
      const complementary = await Promise.all(seeds.map((id) => this.#fetch(id, 'complementary')));
      complementary.forEach(take);
      for (const id of seeds) {
        if (picked.size >= max) break;
        take(await this.#fetch(id, 'related'));
      }
    } catch (error) {
      // Leave whatever is showing; the next open or cart change tries again.
      if (key === this.#loadedKey) this.#loadedKey = '';
      return;
    }

    // The cart changed while these were loading; that newer load wins.
    if (key !== this.#loadedKey) return;
    this.#show([...picked.values()]);
  }

  /**
   * @param {string} productId
   * @param {'complementary' | 'related'} intent
   * @returns {Promise<Document>}
   */
  #fetch(productId, intent) {
    const params = new URLSearchParams({
      product_id: productId,
      limit: '10',
      intent,
      section_id: this.dataset.section || 'lf-cart-upsell',
    });
    const url = `${this.dataset.url || `${ROOT}recommendations/products`}?${params}`;
    let request = this.#cache.get(url);
    if (!request) {
      request = fetch(url)
        .then((response) => {
          if (!response.ok) throw new Error(`recommendations ${response.status}`);
          return response.text();
        })
        .then((html) => new DOMParser().parseFromString(html, 'text/html'));
      request.catch(() => this.#cache.delete(url));
      this.#cache.set(url, request);
    }
    return request;
  }

  /** @param {Element[]} items */
  #show(items) {
    const body = /** @type {HTMLElement | null} */ (this.querySelector('.lf-cart-upsell__body'));
    const list = this.querySelector('.lf-cart-upsell__list');
    if (!body || !list) return;
    list.replaceChildren(...items.map((item) => document.importNode(item, true)));
    body.hidden = items.length === 0;
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-lf-upsell-add]') : null;
    if (!(button instanceof HTMLButtonElement)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (button.getAttribute('aria-busy') === 'true') return;
    this.#add(button);
  };

  /** @param {HTMLButtonElement} button */
  async #add(button) {
    const variantId = Number(button.dataset.variantId);
    const status = /** @type {HTMLElement | null} */ (
      button.closest('[data-lf-upsell-product]')?.querySelector('.lf-cart-upsell__error') || null
    );
    button.setAttribute('aria-busy', 'true');
    button.disabled = true;
    if (status) status.textContent = '';

    try {
      const response = await fetch(`${ROOT}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: variantId, quantity: 1 }],
          sections: this.dataset.cartSection || 'cart-drawer-section',
          sections_url: window.location.pathname,
        }),
      });
      const body = await response.json();
      if (!response.ok) throw new Error(body.description || body.message || '');

      const deferred = CartLinesUpdateEvent.createPromise();
      this.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: [{ merchandiseId: String(variantId), quantity: 1 }],
          promise: deferred.promise,
        })
      );
      const cart = await fetch(`${ROOT}cart.js`).then((res) => res.json());
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { sections: body.sections || {}, source: 'lf-cart-upsell' },
      });
      // The drawer morph changes data-product-ids, which reloads this list
      // without the product just added. Until then the button stays busy.
    } catch (error) {
      if (status) {
        const message = error instanceof Error ? error.message : '';
        status.textContent = message || status.dataset.errorText || '';
      }
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('lf-cart-upsell')) {
  customElements.define('lf-cart-upsell', LfCartUpsell);
}
